import { useRef, type ComponentType, type KeyboardEvent } from 'react';

/**
 * The CRM screen's tab bar (W4 review, REV-W4-F14, F15).
 *
 * - `resolveCrmTab`: the shown tab is always one of the tabs on offer. A tab
 *   chosen under another customer (« Correspondance », Swoft and admins only)
 *   used to survive a switch to a HubSpot customer and showed an empty page.
 * - `CrmTabBar`: WAI-ARIA tabs. Each tab names its panel (`aria-controls`),
 *   only the selected tab is in the Tab order, and Left / Right / Home / End
 *   move between tabs (automatic activation).
 */
export interface CrmTab {
  id: string;
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
}

export function resolveCrmTab(tabs: readonly CrmTab[], active: string, fallback = 'pipeline'): string {
  if (tabs.some((tab) => tab.id === active)) return active;
  return tabs.some((tab) => tab.id === fallback) ? fallback : (tabs[0]?.id ?? fallback);
}

export const crmTabId = (prefix: string, id: string) => `${prefix}-tab-${id}`;
export const crmPanelId = (prefix: string) => `${prefix}-panel`;

export function CrmTabBar({ tabs, active, onChange, label, t, idPrefix = 'crm' }: {
  tabs: readonly CrmTab[];
  /** Already resolved (resolveCrmTab). */
  active: string;
  onChange(id: string): void;
  label: string;
  t(key: string): string;
  idPrefix?: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const next = e.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
      : e.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : null;
    if (next === null) return;
    e.preventDefault();
    const tab = tabs[next];
    if (!tab) return;
    onChange(tab.id);
    refs.current[tab.id]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className="flex items-center gap-1 bg-muted/50 rounded-xl p-1 flex-shrink-0 overflow-x-auto">
      {tabs.map((tab, index) => {
        const Icon = tab.icon;
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            ref={(el) => { refs.current[tab.id] = el; }}
            id={crmTabId(idPrefix, tab.id)}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={crmPanelId(idPrefix)}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={`flex min-h-11 items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--vf-accent)] ${
              isActive
                ? 'bg-background text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden />
            {t(tab.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
